// [BRIQUE-CAISSE-POS] - Module Caisse style Square POS / SumUp
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { BrowserMultiFormatReader } from "@zxing/browser";
import emailjs from "@emailjs/browser";
import { 
  ShoppingCart, Plus, Minus, Trash2, Smartphone,
  Users, Search, Package, Receipt, X, Check, Camera, ScanLine,
  SwitchCamera, Flashlight, FlashlightOff, ChevronUp, ChevronDown,
  Percent, LayoutGrid, Loader2, UtensilsCrossed
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Label } from "../components/ui/label";
import ProductPhoto from "../components/ProductPhoto";
import { getFeatures } from "../lib/boutiqueType";
import { PAYMENT_METHODS, enabledOperators } from "../lib/payments";
import { PaymentOperatorPicker } from "../components/sales/PaymentOperatorPicker";
import {
  ManualEntryDialog, DiscountDialog, VariantDialog,
  ScannerDialog, InvoiceDialog, TableOrdersDialog
} from "../components/sales/SalesDialogs";

// [BRIQUE-CAISSE-POS] Composant principal
export default function Sales() {
  const { getAuthHeaders, user } = useAuth();
  const userRole = localStorage.getItem("userRole") || "owner";
  // [PROFIL-CAISSE] Feature flags
  const features = getFeatures();
  
  // [BRIQUE-CAISSE-POS] États des données
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // [BRIQUE-CAISSE-POS] États UI
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [categoryAnimating, setCategoryAnimating] = useState(false);
  
  // [BRIQUE-CAISSE-POS] États paiement
  const [paymentMethod, setPaymentMethod] = useState("cash");
  // [PAIEMENTS] Opérateur Mobile Money / carte + référence transaction
  const [paymentOperator, setPaymentOperator] = useState(null);
  const [paymentOperatorLabel, setPaymentOperatorLabel] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [amountReceived, setAmountReceived] = useState("");
  const [processing, setProcessing] = useState(false);
  const [discount, setDiscount] = useState({ type: null, value: 0 });
  const [discountOpen, setDiscountOpen] = useState(false);
  const [discountInput, setDiscountInput] = useState("");
  
  // [BRIQUE-CAISSE-POS] États Mobile
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  
  // [BRIQUE-CAISSE-POS] Modale saisie manuelle
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [manualEntry, setManualEntry] = useState({
    designation: "",
    unitPrice: "",
    quantity: 1,
    category: ""
  });
  
  // [BRIQUE-CAISSE-POS] Sélection variante
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  // [BRIQUE-CAISSE-POS] Scanner
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [scanFeedback, setScanFeedback] = useState(null);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [useFrontCamera, setUseFrontCamera] = useState(false);
  const [availableCameras, setAvailableCameras] = useState([]);
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const streamRef = useRef(null);
  const lastScannedRef = useRef(null);
  const scanTimeoutRef = useRef(null);
  
  // [BRIQUE-CAISSE-POS] Facture
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [lastInvoice, setLastInvoice] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [storeSettings, setStoreSettings] = useState(null);
  const [customerEmail, setCustomerEmail] = useState("");
  
  // [SERVEUR-1] Commandes en salle
  const [tableOrders, setTableOrders] = useState([]);
  const [tableOrdersOpen, setTableOrdersOpen] = useState(false);
  const [loadedOrderId, setLoadedOrderId] = useState(null);
  
  // [BRIQUE-CAISSE-POS] Lecture données boutique depuis localStorage
  const boutiqueData = JSON.parse(localStorage.getItem("boutique") || "{}");
  const devise = boutiqueData.currencySymbol || "€";
  const primaryColor = boutiqueData.customColor || boutiqueData.colorPalette?.[0] || "#D4A853";

  // [BRIQUE-CAISSE-POS] Chargement des données
  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/products`, getAuthHeaders()),
        axios.get(`${API}/categories`, getAuthHeaders()),
        axios.get(`${API}/settings`, getAuthHeaders()).catch(() => ({ data: null }))
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setStoreSettings(settingsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    return () => stopScanner();
  }, []);

  // [SERVEUR-1] Poll pending table orders
  const fetchTableOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/table-orders?status=en_attente`, getAuthHeaders());
      setTableOrders(res.data.orders || []);
    } catch (e) {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTableOrders();
    const iv = setInterval(fetchTableOrders, 15000);
    return () => clearInterval(iv);
  }, [fetchTableOrders]);

  // [SERVEUR-1] Load a table order into the cart
  const loadTableOrder = (order) => {
    const newCart = order.items.map((item, idx) => {
      const product = products.find(p => p.id === item.product_id);
      const variant = product?.variants?.find(v => v.id === item.variant_id);
      return {
        cart_key: item.variant_id ? `${item.product_id}-${item.variant_id}` : `order-${order.id}-${idx}`,
        product_id: item.product_id,
        variant_id: item.variant_id,
        product_name: item.product_name,
        size: item.size,
        color: item.color,
        color_hex: variant?.color_hex || null,
        barcode: variant?.barcode || null,
        category_name: product?.category_name || "",
        quantity: item.quantity,
        unit_price: item.unit_price,
        total: item.total,
        max_quantity: product?.track_stock === false ? 999999 : (variant?.quantity ?? 999),
        isManual: !variant
      };
    });
    setCart(newCart);
    setLoadedOrderId(order.id);
    setTableOrdersOpen(false);
    toast.success(`Commande "${order.table_name}" chargée — pris par ${order.serveur?.name || "serveur"}`);
  };

  const cancelTableOrder = async (orderId) => {
    try {
      await axios.put(`${API}/table-orders/${orderId}/status`, { status: "annulee" }, getAuthHeaders());
      toast.success("Commande annulée");
      fetchTableOrders();
    } catch (e) {
      toast.error("Erreur lors de l'annulation");
    }
  };

  // [BRIQUE-CAISSE-POS] Animation changement catégorie
  const handleCategoryChange = (categoryId) => {
    if (categoryId === activeCategory) return;
    setCategoryAnimating(true);
    setTimeout(() => {
      setActiveCategory(categoryId);
      setTimeout(() => setCategoryAnimating(false), 150);
    }, 150);
  };

  // [BRIQUE-CAISSE-POS] Filtrage produits
  const filteredProducts = products.filter(p => {
    const matchesSearch = searchTerm === "" || 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.supplier_ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.category_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === "all" || p.category_id === activeCategory;
    return matchesSearch && matchesCategory;
  });

  // [BRIQUE-CAISSE-POS] Sons et vibrations
  const playBeepSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 1800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (e) {}
  }, []);

  const vibrateDevice = useCallback(() => {
    if (navigator.vibrate) navigator.vibrate(200);
  }, []);

  // [BRIQUE-CAISSE-POS] Gestion Scanner
  const toggleTorch = useCallback(async () => {
    if (!streamRef.current) return;
    const track = streamRef.current.getVideoTracks()[0];
    if (!track) return;
    try {
      const capabilities = track.getCapabilities();
      if (capabilities.torch) {
        const newTorchState = !torchEnabled;
        await track.applyConstraints({ advanced: [{ torch: newTorchState }] });
        setTorchEnabled(newTorchState);
      }
    } catch (error) {}
  }, [torchEnabled]);

  const startScanner = async (preferFront = false) => {
    setScannerOpen(true);
    setCameraLoading(true);
    setCameraError(null);
    setScanFeedback(null);
    setTorchEnabled(false);
    
    try {
      const constraints = {
        video: {
          facingMode: preferFront ? "user" : { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };
      
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (firstError) {
        if (!preferFront) {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } }
          });
          setUseFrontCamera(true);
        } else {
          throw firstError;
        }
      }
      
      streamRef.current = stream;
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(d => d.kind === 'videoinput');
      setAvailableCameras(cameras);
      
      const track = stream.getVideoTracks()[0];
      if (track) {
        const capabilities = track.getCapabilities();
        setTorchSupported(!!capabilities.torch);
      }
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      
      const codeReader = new BrowserMultiFormatReader();
      codeReaderRef.current = codeReader;
      
      const scanInterval = setInterval(async () => {
        if (!videoRef.current || !codeReaderRef.current) {
          clearInterval(scanInterval);
          return;
        }
        try {
          const result = await codeReaderRef.current.decodeFromVideoElement(videoRef.current);
          if (result) {
            const barcode = result.getText();
            if (lastScannedRef.current !== barcode || Date.now() - (scanTimeoutRef.current || 0) > 1500) {
              lastScannedRef.current = barcode;
              scanTimeoutRef.current = Date.now();
              handleBarcodeScanned(barcode);
            }
          }
        } catch (e) {}
      }, 66);
      
      codeReaderRef.current._scanInterval = scanInterval;
      setCameraLoading(false);
      setScanning(true);
      
    } catch (error) {
      setCameraLoading(false);
      if (error.name === 'NotAllowedError') {
        setCameraError("Autorisez l'accès à la caméra");
      } else if (error.name === 'NotFoundError') {
        setCameraError("Aucune caméra détectée");
      } else {
        setCameraError("Erreur caméra");
      }
    }
  };

  const switchCamera = async () => {
    stopScanner(false);
    setUseFrontCamera(!useFrontCamera);
    await startScanner(!useFrontCamera);
  };

  const stopScanner = (closeDialog = true) => {
    if (codeReaderRef.current && codeReaderRef.current._scanInterval) {
      clearInterval(codeReaderRef.current._scanInterval);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (codeReaderRef.current) {
      try { codeReaderRef.current.reset(); } catch (e) {}
      codeReaderRef.current = null;
    }
    setScanning(false);
    setTorchEnabled(false);
    setTorchSupported(false);
    setCameraLoading(false);
    setCameraError(null);
    lastScannedRef.current = null;
    if (closeDialog) {
      setScannerOpen(false);
      setScanFeedback(null);
    }
  };

  // [BRIQUE-CAISSE-POS] Gestion code-barres scanné
  const handleBarcodeScanned = async (barcode) => {
    try {
      const response = await axios.get(`${API}/products/barcode/${barcode}`, getAuthHeaders());
      const { product, scanned_variant } = response.data;
      
      playBeepSound();
      vibrateDevice();
      
      if (scanned_variant) {
        setScanFeedback({ type: 'success', message: `${product.name} - ${scanned_variant.size}` });
        addToCartWithVariant(product, scanned_variant);
        toast.success(`✓ ${product.name} ajouté`);
      } else {
        setSelectedProduct(product);
        setVariantDialogOpen(true);
        stopScanner();
      }
      
      setTimeout(() => setScanFeedback(null), 1500);
    } catch (error) {
      vibrateDevice();
      setScanFeedback({ type: 'error', message: `Non trouvé: ${barcode}` });
      toast.error(`Article non trouvé: ${barcode}`);
      setTimeout(() => setScanFeedback(null), 2000);
    }
  };

  // [BRIQUE-CAISSE-POS] Click sur produit
  const handleProductClick = (product) => {
    if (!product.variants || product.variants.length === 0) {
      toast.error("Aucune variante disponible");
      return;
    }
    
    // [PROFIL-CAISSE] Stock non suivi : ajout direct sans contrôle de quantité
    if (product.track_stock === false) {
      addToCartWithVariant(product, product.variants[0]);
      toast.success(`✓ ${product.name} ajouté`, { duration: 1500 });
      return;
    }

    // Produits avec stock disponible
    const availableVariants = product.variants.filter(v => v.quantity > 0);
    
    if (availableVariants.length === 0) {
      toast.error("Article en rupture de stock");
      return;
    }
    
    if (availableVariants.length === 1) {
      addToCartWithVariant(product, availableVariants[0]);
      toast.success(`✓ ${product.name} ajouté`, { duration: 1500 });
    } else {
      setSelectedProduct(product);
      setSelectedVariant(null);
      setVariantDialogOpen(true);
    }
  };

  // [BRIQUE-CAISSE-POS] Ajout au panier avec variante
  const addToCartWithVariant = (product, variant) => {
    const tracked = product.track_stock !== false;
    if (tracked && variant.quantity <= 0) {
      toast.error("Rupture de stock");
      return;
    }
    
    const cartKey = `${product.id}-${variant.id}`;
    
    setCart(prev => {
      const existing = prev.find(item => item.cart_key === cartKey);
      
      if (existing) {
        if (tracked && existing.quantity >= variant.quantity) {
          toast.error("Stock insuffisant");
          return prev;
        }
        return prev.map(item =>
          item.cart_key === cartKey
            ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.unit_price }
            : item
        );
      }
      
      return [...prev, {
        cart_key: cartKey,
        product_id: product.id,
        variant_id: variant.id,
        product_name: product.name,
        size: variant.size,
        color: variant.color,
        color_hex: variant.color_hex,
        barcode: variant.barcode,
        category_name: product.category_name,
        quantity: 1,
        unit_price: product.sale_price,
        total: product.sale_price,
        max_quantity: tracked ? variant.quantity : 999999,
        isManual: false
      }];
    });
    
    setVariantDialogOpen(false);
  };

  const confirmVariantSelection = () => {
    if (selectedProduct && selectedVariant) {
      addToCartWithVariant(selectedProduct, selectedVariant);
    } else {
      toast.error("Sélectionnez une taille");
    }
  };

  // [BRIQUE-CAISSE-POS] Saisie manuelle
  const handleManualEntry = () => {
    if (!manualEntry.designation || !manualEntry.unitPrice) {
      toast.error("Remplissez la désignation et le prix");
      return;
    }
    
    const price = parseFloat(manualEntry.unitPrice);
    if (isNaN(price) || price <= 0) {
      toast.error("Prix invalide");
      return;
    }
    
    const cartKey = `manual-${Date.now()}`;
    const categoryName = categories.find(c => c.id === manualEntry.category)?.name || "";
    
    setCart(prev => [...prev, {
      cart_key: cartKey,
      product_id: null,
      variant_id: null,
      product_name: manualEntry.designation,
      size: null,
      color: null,
      color_hex: null,
      barcode: null,
      category_name: categoryName,
      quantity: manualEntry.quantity,
      unit_price: price,
      total: price * manualEntry.quantity,
      max_quantity: 999,
      isManual: true
    }]);
    
    toast.success(`✓ ${manualEntry.designation} ajouté`);
    setManualEntry({ designation: "", unitPrice: "", quantity: 1, category: "" });
    setManualEntryOpen(false);
  };

  // [BRIQUE-CAISSE-POS] Gestion quantités panier
  const updateCartQuantity = (cartKey, delta) => {
    setCart(prev =>
      prev.map(item => {
        if (item.cart_key === cartKey) {
          const newQty = item.quantity + delta;
          if (newQty <= 0) return item;
          if (!item.isManual && newQty > item.max_quantity) {
            toast.error("Stock insuffisant");
            return item;
          }
          return { ...item, quantity: newQty, total: newQty * item.unit_price };
        }
        return item;
      })
    );
  };

  const removeFromCart = (cartKey) => {
    setCart(prev => prev.filter(item => item.cart_key !== cartKey));
  };

  const clearCart = () => {
    setCart([]);
    setDiscount({ type: null, value: 0 });
    toast.success("Panier vidé");
  };

  // [BRIQUE-CAISSE-POS] Calculs totaux
  const cartSubtotal = cart.reduce((sum, item) => sum + item.total, 0);
  const discountAmount = discount.type === "percent" 
    ? cartSubtotal * (discount.value / 100)
    : discount.type === "fixed" 
      ? discount.value 
      : 0;
  const cartTotal = Math.max(0, cartSubtotal - discountAmount);
  const cartItemsCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const changeAmount = paymentMethod === "cash" && amountReceived 
    ? parseFloat(amountReceived) - cartTotal 
    : 0;

  // [BRIQUE-CAISSE-POS] Gestion remise
  const applyDiscount = (type, value) => {
    if (type === "percent") {
      const pct = parseFloat(value);
      if (!isNaN(pct) && pct > 0 && pct <= 100) {
        setDiscount({ type: "percent", value: pct });
        setDiscountOpen(false);
        setDiscountInput("");
      }
    } else if (type === "fixed") {
      const amount = parseFloat(value);
      if (!isNaN(amount) && amount > 0 && amount <= cartSubtotal) {
        setDiscount({ type: "fixed", value: amount });
        setDiscountOpen(false);
        setDiscountInput("");
      }
    }
  };

  const removeDiscount = () => {
    setDiscount({ type: null, value: 0 });
  };

  // [BRIQUE-CAISSE-POS] Validation vente
  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error("Le panier est vide");
      return;
    }
    
    if (paymentMethod === "cash") {
      const received = amountReceived === "" ? cartTotal : parseFloat(amountReceived);
      if (Number.isNaN(received) || received < cartTotal) {
        toast.error(`Montant reçu insuffisant (total ${cartTotal.toLocaleString("fr-FR")} ${devise})`);
        return;
      }
      if (amountReceived === "") setAmountReceived(String(cartTotal));
    } else if (!paymentOperator) {
      toast.error("Choisissez l'opérateur de paiement (Orange Money, Wave…)");
      return;
    } else if (paymentOperator === "other" && !paymentOperatorLabel.trim()) {
      toast.error("Précisez le moyen de paiement");
      return;
    }

    setProcessing(true);
    try {
      const invoiceData = {
        items: cart.map(item => ({
          product_id: item.product_id,
          variant_id: item.variant_id,
          product_name: item.product_name,
          size: features.variants ? item.size : null,
          color: features.variants ? item.color : null,
          barcode: item.barcode,
          category_name: item.category_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total: item.total
        })),
        subtotal: cartSubtotal,
        discount_type: discount.type,
        discount_value: discount.value,
        discount_amount: discountAmount,
        total: cartTotal,
        payment_method: paymentMethod,
        payment_operator: paymentMethod === "cash" ? null : paymentOperator,
        payment_operator_label: paymentOperator === "other" ? paymentOperatorLabel.trim() : null,
        payment_reference: paymentMethod === "cash" ? null : (paymentReference.trim() || null),
        amount_received: paymentMethod === "cash" ? (amountReceived === "" ? cartTotal : parseFloat(amountReceived)) : null,
        change_given: paymentMethod === "cash" ? (amountReceived === "" ? 0 : changeAmount) : null,
        customer_email: customerEmail || null,
        // [LIVE-VENTES] Vendeur info
        vendeur: {
          id: sessionStorage.getItem("userId") || user?.id || "unknown",
          prenom: sessionStorage.getItem("prenom") || user?.name?.split(' ')[0] || "Vendeur",
          role: userRole
        }
      };

      const response = await axios.post(`${API}/invoices`, invoiceData, getAuthHeaders());
      setLastInvoice(response.data);
      
      // [SERVEUR-1] Mark loaded table order as cashed in
      if (loadedOrderId) {
        try {
          await axios.put(`${API}/table-orders/${loadedOrderId}/status`, { status: "encaissee" }, getAuthHeaders());
          fetchTableOrders();
        } catch (e) {
          toast.error("La commande salle n'a pas pu être marquée encaissée");
        }
        setLoadedOrderId(null);
      }
      
      // Reset
      setCart([]);
      setAmountReceived("");
      setPaymentOperator(null);
      setPaymentOperatorLabel("");
      setPaymentReference("");
      setCustomerEmail("");
      setDiscount({ type: null, value: 0 });
      setInvoiceDialogOpen(true);
      
      fetchData();
      toast.success("🎉 Vente enregistrée !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la vente");
    } finally {
      setProcessing(false);
    }
  };

  // [BRIQUE-CAISSE-POS] Formatage prix
  const formatPrice = (price) => {
    return `${price.toLocaleString('fr-FR')} ${devise}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
    <style>{`
      .caisse-cat-scroll::-webkit-scrollbar { display: none; }
    `}</style>
    <div className="h-[calc(100vh-7rem)] flex flex-col lg:flex-row gap-4" data-testid="sales-page">
      {/* [BRIQUE-CAISSE-POS] COLONNE GAUCHE - CATALOGUE */}
      <div className="flex-1 lg:w-[60%] flex flex-col min-h-0">
        
        {/* [BRIQUE-CAISSE-POS] Barre de recherche + Saisie manuelle */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher ou scanner un article..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-12"
              data-testid="search-input"
            />
            <button
              onClick={() => startScanner()}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-secondary transition-colors"
              title="Scanner"
            >
              <ScanLine className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
          <Button 
            onClick={() => setManualEntryOpen(true)}
            style={{ backgroundColor: '#FF4500' }}
            className="text-white shrink-0"
            data-testid="manual-entry-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Saisie manuelle</span>
            <span className="sm:hidden">+</span>
          </Button>
          {features.tableOrders && (
          <Button
            onClick={() => setTableOrdersOpen(true)}
            variant="outline"
            className="shrink-0 relative"
            data-testid="table-orders-btn"
          >
            <UtensilsCrossed className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">Salle</span>
            {tableOrders.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center" style={{ fontSize: '10px' }}>
                {tableOrders.length}
              </span>
            )}
          </Button>
          )}
        </div>

        {/* [BRIQUE-CAISSE-POS] Onglets catégories — scrollable horizontal, sans overflow */}
        <div className="mb-4">
          <div
            className="caisse-cat-scroll"
            style={{
              display:         'flex',
              gap:             '8px',
              flexWrap:        'wrap',
              paddingBottom:   '4px',
            }}
          >
            {/* "Tous" toujours en premier et toujours visible */}
            <button
              onClick={() => handleCategoryChange("all")}
              className="px-4 py-2 rounded-[10px] text-sm font-medium whitespace-nowrap transition-all flex items-center"
              style={activeCategory === "all"
                ? { backgroundColor: '#FF4500', color: '#FFFFFF' }
                : { backgroundColor: 'rgba(255,69,0,0.08)', border: '1px solid rgba(255,69,0,0.20)', color: 'inherit' }
              }
              data-testid="category-all"
            >
              <LayoutGrid className="w-4 h-4 inline mr-1.5" />
              Tous
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className="px-4 py-2 rounded-[10px] text-sm font-medium whitespace-nowrap transition-all flex items-center"
                style={activeCategory === cat.id
                  ? { backgroundColor: '#FF4500', color: '#FFFFFF' }
                  : { backgroundColor: 'rgba(255,69,0,0.08)', border: '1px solid rgba(255,69,0,0.20)', color: 'inherit' }
                }
                data-testid={`category-${cat.id}`}
              >
                <span className="mr-1.5">{cat.icon}</span>
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* [BRIQUE-CAISSE-POS] Grille de produits */}
        <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100vh - 180px)' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0 }}
              animate={{ opacity: categoryAnimating ? 0 : 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', paddingBottom: '8px' }}
            >
              {filteredProducts.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <Package className="w-12 h-12 mx-auto mb-3 text-gray-500" />
                  <p className="text-gray-500">Aucun article trouvé</p>
                </div>
              ) : (
                filteredProducts.map((product) => {
                  const totalStock = product.total_quantity || 0;
                  const tracked = product.track_stock !== false;
                  const isOutOfStock = tracked && totalStock === 0;
                  
                  return (
                    <motion.div
                      key={product.id}
                      whileTap={{ scale: isOutOfStock ? 1 : 0.96 }}
                      transition={{ duration: 0.15 }}
                      onClick={() => !isOutOfStock && handleProductClick(product)}
                      className={`group relative rounded-lg overflow-hidden cursor-pointer transition-all border bg-card ${
                        isOutOfStock 
                          ? "opacity-40 cursor-not-allowed" 
                          : ""
                      }`}
                      data-testid={`product-${product.id}`}
                    >
                      {/* Photo produit */}
                      <div className="relative" style={{ aspectRatio: '4/3' }}>
                        <ProductPhoto 
                          productId={product.id}
                          backendImage={product.image}
                          categoryName={product.category_name}
                          categoryIcon={product.category_icon}
                          size="full"
                          aspectRatio="4/3"
                          className="w-full h-full object-cover"
                        />
                        
                        {/* Stock badge */}
                        {tracked && (
                        <span className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-black/60 text-white" style={{ fontSize: '9px' }}>
                          {totalStock}
                        </span>
                        )}

                        {/* Rupture badge */}
                        {isOutOfStock && (
                          <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-red-600 text-white" style={{ fontSize: '9px' }}>
                            Rupture
                          </span>
                        )}

                        {/* Orange hover overlay */}
                        {!isOutOfStock && (
                          <div className="absolute inset-0 bg-orange-500/0 group-hover:bg-orange-500/20 transition-colors duration-150" />
                        )}
                      </div>
                      
                      {/* Infos produit */}
                      <div style={{ padding: '8px' }}>
                        <h3 style={{ fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{product.name}</h3>
                        <p className="text-muted-foreground" style={{ fontSize: '10px', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {features.variants
                            ? (product.variants?.filter(v => v.quantity > 0).map(v => v.size).join(", ") || "—")
                            : (product.category_name || "—")}
                        </p>
                        <span style={{ fontSize: '12px', fontWeight: 700 }}>{formatPrice(product.sale_price)}</span>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* [BRIQUE-CAISSE-POS] COLONNE DROITE - PANIER (Desktop) */}
      <Card className="hidden lg:flex lg:w-[40%] flex-col">
        {/* Header panier */}
        <div className="flex items-center justify-between border-b" style={{ padding: '12px 16px', fontSize: '14px' }}>
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" />
            <span className="font-semibold">Panier</span>
            {cartItemsCount > 0 && (
              <Badge style={{ backgroundColor: primaryColor, fontSize: '11px' }} className="text-white">
                {cartItemsCount}
              </Badge>
            )}
          </div>
          {cart.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearCart}
              className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 text-xs"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Vider
            </Button>
          )}
        </div>
        
        {/* Liste articles */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 340px)' }}>
          {cart.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ShoppingCart className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Panier vide</p>
              <p className="text-xs mt-1">Cliquez sur un article pour l'ajouter</p>
            </div>
          ) : (
            <div>
              {cart.map((item) => (
                <div
                  key={item.cart_key}
                  className="relative border-b"
                  style={{ padding: '8px 12px', borderColor: 'var(--border)' }}
                  data-testid={`cart-item-${item.cart_key}`}
                >
                  {/* Remove button */}
                  <button
                    onClick={() => removeFromCart(item.cart_key)}
                    className="absolute text-muted-foreground hover:text-destructive transition-colors"
                    style={{ top: '8px', right: '8px', fontSize: '14px', lineHeight: 1 }}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>

                  <div className="flex items-center justify-between pr-5">
                    <div className="flex-1 min-w-0">
                      <p style={{ fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.product_name}</p>
                      {features.variants && item.size && (
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {item.color_hex && (
                            <div 
                              className="rounded-full border"
                              style={{ width: '8px', height: '8px', backgroundColor: item.color_hex }}
                            />
                          )}
                          <span className="text-muted-foreground" style={{ fontSize: '11px' }}>
                            {item.size}{item.color ? ` · ${item.color}` : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-1.5">
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => updateCartQuantity(item.cart_key, -1)}
                        disabled={item.quantity <= 1}
                        className="flex items-center justify-center rounded border bg-secondary disabled:opacity-40"
                        style={{ width: '20px', height: '20px', fontSize: '12px' }}
                      >
                        −
                      </button>
                      <span className="text-center font-medium" style={{ width: '28px', fontSize: '12px' }}>{item.quantity}</span>
                      <button
                        onClick={() => updateCartQuantity(item.cart_key, 1)}
                        className="flex items-center justify-center rounded border bg-secondary"
                        style={{ width: '20px', height: '20px', fontSize: '12px' }}
                      >
                        +
                      </button>
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: 600 }}>{formatPrice(item.total)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section totaux et paiement */}
        <div className="border-t" style={{ padding: '10px 16px' }}>
          {/* Totaux */}
          <div className="space-y-1.5" style={{ fontSize: '12px' }}>
            <div className="flex justify-between text-muted-foreground">
              <span>Sous-total</span>
              <span>{formatPrice(cartSubtotal)}</span>
            </div>
            
            {/* Remise */}
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Remise</span>
              {discount.type ? (
                <div className="flex items-center gap-2">
                  <span className="text-green-600">− {formatPrice(discountAmount)}</span>
                  <button 
                    onClick={removeDiscount}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setDiscountOpen(true)}
                  className="h-auto py-0.5 px-2"
                  style={{ fontSize: '11px' }}
                  disabled={cart.length === 0}
                >
                  <Percent className="w-3 h-3 mr-1" />
                  Ajouter
                </Button>
              )}
            </div>
            
            <div className="border-t pt-1.5">
              <div className="flex justify-between font-bold" style={{ fontSize: '15px' }}>
                <span>TOTAL</span>
                <span>{formatPrice(cartTotal)}</span>
              </div>
            </div>
          </div>

          {/* Modes de paiement */}
          <div className="mt-3 space-y-1.5">
            <Label className="text-muted-foreground" style={{ fontSize: '10px' }}>Mode de paiement</Label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
              {PAYMENT_METHODS.map(({ id, icon: Icon, label, color }) => {
                const isActive = paymentMethod === id;
                return (
                  <button
                    key={id}
                    onClick={() => setPaymentMethod(id)}
                    data-testid={`payment-btn-${id}`}
                    className="payment-btn"
                    style={{
                      background: id === "cash" ? '#111111' : (isActive ? '#FF4500' : color),
                      borderColor: isActive ? '#FF4500' : (id === "cash" ? '#2A2A2A' : color),
                      borderWidth: isActive ? 2 : 1,
                      opacity: !isActive && id === "cash" ? 0.75 : 1,
                      boxShadow: isActive
                        ? '0 4px 16px -4px rgba(255,69,0,0.7)'
                        : (id === "cash" ? '0 4px 14px -4px rgba(0,0,0,0.5)' : '0 4px 14px -4px rgba(34,197,94,0.45)'),
                      transform: isActive ? 'translateY(-1px)' : 'none',
                      position: 'relative',
                    }}
                  >
                    {isActive && <Check style={{ position: 'absolute', top: 4, right: 4, width: 11, height: 11, color: id === "cash" ? '#FF4500' : '#ffffff' }} />}
                    <Icon style={{ width: '16px', height: '16px', color: '#ffffff' }} />
                    <span style={{ fontSize: '10px', color: '#ffffff', fontWeight: 600, textAlign: 'center', lineHeight: 1.1 }}>
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* [PAIEMENTS] Opérateur Mobile Money / carte */}
          {paymentMethod === "card" && (
            <PaymentOperatorPicker
              operators={enabledOperators(storeSettings)}
              value={paymentOperator} onChange={setPaymentOperator}
              otherLabel={paymentOperatorLabel} onOtherLabelChange={setPaymentOperatorLabel}
              reference={paymentReference} onReferenceChange={setPaymentReference}
              primaryColor={primaryColor}
            />
          )}

          {/* Champ montant espèces */}
          {paymentMethod === "cash" && (
            <div className="mt-2 space-y-1.5">
              <Label className="text-muted-foreground" style={{ fontSize: '10px' }}>Montant remis</Label>
              <Input
                type="number"
                step="1"
                value={amountReceived}
                onChange={(e) => setAmountReceived(e.target.value)}
                placeholder={`0 ${devise}`}
                className="h-9"
                style={{ fontSize: '14px' }}
                data-testid="cash-amount-input"
              />
              {changeAmount > 0 && (
                <div className="p-2 rounded-lg text-center" style={{ backgroundColor: '#FF4500' }}>
                  <p style={{ color: '#FFFFFF', fontSize: '10px' }}>Monnaie à rendre</p>
                  <p className="font-bold" style={{ color: '#FFFFFF', fontSize: '18px' }}>{formatPrice(changeAmount)}</p>
                </div>
              )}
            </div>
          )}

          {/* Bouton validation */}
          <Button
            onClick={handleCheckout}
            disabled={cart.length === 0 || processing || (paymentMethod === "card" && (!paymentOperator || (paymentOperator === "other" && !paymentOperatorLabel.trim())))}
            className="w-full text-white transition-transform active:scale-[0.97] mt-3"
            style={{ backgroundColor: '#FF4500', padding: '12px', fontSize: '13px' }}
            data-testid="checkout-btn"
          >
            {processing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Valider la vente
          </Button>
        </div>
      </Card>

      {/* [BRIQUE-CAISSE-POS] PANIER MOBILE - Mini résumé sticky */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50">
        <AnimatePresence>
          {mobileCartOpen ? (
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-background border-t max-h-[80vh] overflow-hidden flex flex-col"
            >
              {/* Header mobile cart */}
              <button
                onClick={() => setMobileCartOpen(false)}
                className="flex items-center justify-between p-4 border-b"
              >
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  <span className="font-semibold">Panier ({cartItemsCount})</span>
                </div>
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              </button>
              
              {/* Contenu panier mobile */}
              <ScrollArea className="flex-1 p-4 max-h-[40vh]">
                {cart.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Panier vide</p>
                ) : (
                  <div className="space-y-2">
                    {cart.map((item) => (
                      <div key={item.cart_key} className="flex items-center justify-between p-2 bg-secondary/50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{item.product_name}</p>
                          <p className="text-xs text-muted-foreground">{features.variants && item.size ? `${item.size} × ` : "× "}{item.quantity}</p>
                        </div>
                        <span className="text-sm font-medium ml-2">{formatPrice(item.total)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              
              {/* Totaux et paiement mobile */}
              <div className="p-4 border-t space-y-3">
                <div className="flex justify-between text-lg font-bold">
                  <span>TOTAL</span>
                  <span>{formatPrice(cartTotal)}</span>
                </div>
                
                {/* Modes paiement mobile */}
                <div className="grid grid-cols-2 gap-2">
                  {PAYMENT_METHODS.map(({ id, icon: Icon, label, color }) => (
                    <button
                      key={id}
                      onClick={() => setPaymentMethod(id)}
                      data-testid={`payment-btn-mobile-${id}`}
                      className="p-2 rounded-lg transition-all text-white flex flex-col items-center gap-1"
                      style={{ backgroundColor: id === "cash" ? '#111111' : (paymentMethod === id ? '#FF4500' : color), border: `2px solid ${paymentMethod === id ? '#FF4500' : (id === "cash" ? '#2A2A2A' : color)}`, opacity: paymentMethod !== id && id === "cash" ? 0.75 : 1 }}
                    >
                      <Icon className="w-5 h-5 mx-auto" />
                      <span className="text-[10px] font-semibold leading-tight text-center">{label}</span>
                    </button>
                  ))}
                </div>

                {paymentMethod === "card" && (
                  <PaymentOperatorPicker compact
                    operators={enabledOperators(storeSettings)}
                    value={paymentOperator} onChange={setPaymentOperator}
                    otherLabel={paymentOperatorLabel} onOtherLabelChange={setPaymentOperatorLabel}
                    reference={paymentReference} onReferenceChange={setPaymentReference}
                    primaryColor={primaryColor}
                  />
                )}
                
                {paymentMethod === "cash" && (
                  <Input
                    type="number"
                    value={amountReceived}
                    onChange={(e) => setAmountReceived(e.target.value)}
                    data-testid="cash-amount-input-mobile"
                    placeholder={`Montant reçu (${devise})`}
                    className="h-11"
                  />
                )}
                
                <Button
                  onClick={handleCheckout}
                  disabled={cart.length === 0 || processing || (paymentMethod === "card" && (!paymentOperator || (paymentOperator === "other" && !paymentOperatorLabel.trim())))}
                  className="w-full py-5 text-lg font-bold text-white"
                  style={{ backgroundColor: '#FF4500' }}
                  data-testid="checkout-btn-mobile"
                >
                  {processing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Check className="w-5 h-5 mr-2" />}
                  Valider la vente
                </Button>
              </div>
            </motion.div>
          ) : (
            cart.length > 0 && (
              <motion.button
                initial={{ y: 100 }}
                animate={{ y: 0 }}
                onClick={() => setMobileCartOpen(true)}
                className="w-full p-4 flex items-center justify-between text-white"
                style={{ backgroundColor: primaryColor }}
              >
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  <span className="font-medium">{cartItemsCount} article{cartItemsCount > 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold">{formatPrice(cartTotal)}</span>
                  <ChevronUp className="w-5 h-5" />
                </div>
              </motion.button>
            )
          )}
        </AnimatePresence>
      </div>

      {/* [BRIQUE-CAISSE-POS] DIALOGS */}
      <ManualEntryDialog
        open={manualEntryOpen} onOpenChange={setManualEntryOpen}
        manualEntry={manualEntry} setManualEntry={setManualEntry}
        categories={categories} devise={devise} primaryColor={primaryColor}
        onSubmit={handleManualEntry}
      />
      <DiscountDialog
        open={discountOpen} onOpenChange={setDiscountOpen}
        discountInput={discountInput} setDiscountInput={setDiscountInput}
        primaryColor={primaryColor} onApply={applyDiscount}
      />
      <VariantDialog
        open={variantDialogOpen} onOpenChange={setVariantDialogOpen}
        selectedProduct={selectedProduct} selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant} primaryColor={primaryColor}
        onConfirm={confirmVariantSelection}
      />
      <ScannerDialog
        open={scannerOpen} onClose={stopScanner}
        videoRef={videoRef} cameraLoading={cameraLoading} cameraError={cameraError}
        scanning={scanning} scanFeedback={scanFeedback}
        availableCameras={availableCameras} torchSupported={torchSupported}
        torchEnabled={torchEnabled} switchCamera={switchCamera} toggleTorch={toggleTorch}
      />
      <InvoiceDialog
        open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}
        lastInvoice={lastInvoice} formatPrice={formatPrice} userRole={userRole}
      />
      <TableOrdersDialog
        open={tableOrdersOpen} onOpenChange={setTableOrdersOpen}
        orders={tableOrders} formatPrice={formatPrice}
        onLoad={loadTableOrder} onCancel={cancelTableOrder} primaryColor={primaryColor}
      />
    </div>
    </>
  );
}
